import * as React from 'react';
import type {Intent} from '@sail/engine';

/**
 * The view primitives type - compatible with both stable and deprecated view objects.
 * Uses a permissive type since the deprecated view has different CSS properties than stable.
 */
type ViewPrimitives = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  div: React.ComponentType<any>;
};

/**
 * Value provided by DeprecatedThemeContext when a deprecated theme provider is active.
 *
 * This allows stable @sail/ui components to optionally use deprecated theming
 * capabilities without directly depending on deprecated code.
 */
export type DeprecatedThemeValue = {
  /**
   * The themed view primitives (e.g., view.div) that include deprecated token support.
   */
  view: ViewPrimitives;

  /**
   * Global theme styles that need to be included for deprecated token propagation.
   */
  themeGlobals: Intent;

  /**
   * Hook to get the current token styles for deprecated components.
   */
  useTokenContext: () => {styles: Intent};
};

/**
 * Context for injecting deprecated theming capabilities.
 *
 * This allows stable @sail/ui components (like OverlayOverrides) to optionally
 * use deprecated theming without directly depending on deprecated packages.
 *
 * Usage:
 * - @sail/ui imports DeprecatedThemeContext (no deprecated dependency)
 * - @sail/theme-classic Provider sets the context value
 * - Components check for context value and fall back to stable primitives if null
 *
 * When context is null (no deprecated provider):
 * - Components should use stable `view.div` from @sail/engine
 * - Deprecated token propagation is skipped
 */
export const DeprecatedThemeContext =
  React.createContext<DeprecatedThemeValue | null>(null);
